"use client"

import { createContext, useContext, useEffect, useMemo, useState } from "react"

const STORAGE_KEY = "prompt-market-app-state-v1"

type PersistedState = {
  isLoggedIn: boolean
  favorites: string[]
  cart: Record<string, number>
  purchased: string[]
}

type AppStateContextValue = {
  isLoggedIn: boolean
  favorites: string[]
  cart: Record<string, number>
  purchased: string[]
  cartItemCount: number
  login: () => void
  logout: () => void
  toggleFavorite: (id: string) => void
  addToCart: (id: string) => boolean
  purchasePrompt: (id: string) => void
  setCartQuantity: (id: string, quantity: number) => void
  removeFromCart: (id: string) => void
}

const defaultState: PersistedState = {
  isLoggedIn: false,
  favorites: [],
  cart: {},
  purchased: [],
}

const AppStateContext = createContext<AppStateContextValue | null>(null)

export function AppStateProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<PersistedState>(defaultState)
  const [isHydrated, setIsHydrated] = useState(false)

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) {
        const parsed = JSON.parse(raw) as PersistedState
        setState({
          isLoggedIn: parsed.isLoggedIn ?? false,
          favorites: Array.isArray(parsed.favorites) ? parsed.favorites : [],
          cart: Object.fromEntries(
            Object.entries(parsed.cart ?? {}).filter(([, quantity]) => Number(quantity) > 0).map(([id]) => [id, 1]),
          ),
          purchased: Array.isArray(parsed.purchased) ? parsed.purchased : [],
        })
      }
    } finally {
      setIsHydrated(true)
    }
  }, [])

  useEffect(() => {
    if (!isHydrated) return
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  }, [isHydrated, state])

  const value = useMemo<AppStateContextValue>(() => {
    const cartItemCount = Object.keys(state.cart).length

    return {
      ...state,
      cartItemCount,
      login: () => setState((prev) => ({ ...prev, isLoggedIn: true })),
      logout: () =>
        setState((prev) => ({
          ...prev,
          isLoggedIn: false,
          favorites: [],
          cart: {},
          purchased: [],
        })),
      toggleFavorite: (id) =>
        setState((prev) => ({
          ...prev,
          favorites: prev.favorites.includes(id) ? prev.favorites.filter((item) => item !== id) : [...prev.favorites, id],
        })),
      addToCart: (id) => {
        if (state.cart[id]) {
          return false
        }
        setState((prev) => ({
          ...prev,
          cart: {
            ...prev.cart,
            // Digital products are one-time purchases; keep max quantity at 1.
            [id]: 1,
          },
        }))
        return true
      },
      purchasePrompt: (id) =>
        setState((prev) => {
          const nextCart = { ...prev.cart }
          delete nextCart[id]
          return {
            ...prev,
            cart: nextCart,
            purchased: prev.purchased.includes(id) ? prev.purchased : [...prev.purchased, id],
          }
        }),
      setCartQuantity: (id, quantity) =>
        setState((prev) => {
          const nextCart = { ...prev.cart }
          if (quantity <= 0) {
            delete nextCart[id]
          } else {
            nextCart[id] = 1
          }
          return { ...prev, cart: nextCart }
        }),
      removeFromCart: (id) =>
        setState((prev) => {
          const nextCart = { ...prev.cart }
          delete nextCart[id]
          return { ...prev, cart: nextCart }
        }),
    }
  }, [state])

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>
}

export function useAppState() {
  const context = useContext(AppStateContext)
  if (!context) {
    throw new Error("useAppState must be used within AppStateProvider")
  }
  return context
}
